import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './SuperAdminPanel.css';

function SuperAdminPanel() {
  const { gebruiker, logout } = useAuth();
  const navigate = useNavigate();
  const [bedrijven, setBedrijven] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNieuwBedrijf, setShowNieuwBedrijf] = useState(false);

  useEffect(() => {
    if (gebruiker?.rol !== 'super_admin' && gebruiker?.rol !== 'hoofdbeheerder') {
      navigate('/dashboard');
      return;
    }
    loadBedrijven();
  }, [gebruiker, navigate]);

  const handleToggleAes = async (bedrijfId, currentValue) => {
    const newValue = !currentValue;
    if (!window.confirm(`AES Premium ${newValue ? 'INSCHAKELEN' : 'UITSCHAKELEN'} voor dit bedrijf?\n\nDit ${newValue ? 'activeert' : 'deactiveert'} SMS-OTP verificatie voor handtekeningen (eIDAS Trede 2).`)) return;
    try {
      await api.patch(`/bedrijven/${bedrijfId}/admin-licenties`, { aes_enabled: newValue });
      loadBedrijven();
    } catch (error) {
      alert('Fout: ' + (error.response?.data?.message || error.message));
    }
  };

  const loadBedrijven = async () => {
    try {
      const response = await api.get('/bedrijven');
      setBedrijven(response.data.bedrijven || []);
      setLoading(false);
    } catch (error) {
      console.error('Fout bij laden bedrijven:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (bedrijfId, bedrijfNaam) => {
    if (!window.confirm(`Weet je zeker dat je bedrijf "${bedrijfNaam}" wilt verwijderen?\n\nDit kan niet ongedaan worden gemaakt!`)) {
      return;
    }
    try {
      await api.delete(`/bedrijven/${bedrijfId}`);
      alert('Bedrijf succesvol verwijderd!');
      loadBedrijven();
    } catch (error) {
      alert('Fout bij verwijderen: ' + (error.response?.data?.message || error.message));
    }
  };

  const daysLeft = (eindDatum) => {
    if (!eindDatum) return null;
    const now = new Date();
    const end = new Date(eindDatum);
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };

  const getBadgeClass = (type) => {
    const badges = {
      trial: 'badge-trial',
      basis: 'badge-basis',
      pro: 'badge-pro',
      enterprise: 'badge-enterprise'
    };
    return badges[type] || 'badge-default';
  };

  if (loading) {
    return <div className="loading-screen">Laden...</div>;
  }

  return (
    <div className="super-admin-panel">
      {showNieuwBedrijf && (
        <NieuwBedrijfModal
          onClose={() => setShowNieuwBedrijf(false)}
          onCreated={() => { setShowNieuwBedrijf(false); loadBedrijven(); }}
        />
      )}
      <div className="admin-container">
        <div className="admin-header">
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
            <h1 style={{margin:0,flex:1}}>Alle Bedrijven</h1>
            <button
              style={{padding:'10px 16px',background:'#10b981',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontWeight:600}}
              onClick={() => setShowNieuwBedrijf(true)}
            >
              + Nieuw bedrijf
            </button>
            <button
              style={{padding:'10px 16px',background:'#1e3a5f',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontWeight:500}}
              onClick={() => navigate('/admin/demo-aanvragen')}
            >
              📋 Demo Aanvragen →
            </button>
          </div>
          <div className="admin-stats">
            <div className="stat-card">
              <div className="stat-number">{bedrijven.length}</div>
              <div className="stat-label">Totaal Bedrijven</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{bedrijven.filter(b => b.abonnement === 'trial').length}</div>
              <div className="stat-label">In Trial</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{bedrijven.filter(b => b.actief).length}</div>
              <div className="stat-label">Actief</div>
            </div>
          </div>
        </div>

        <div className="bedrijven-table-wrapper">
          <table className="bedrijven-table">
            <thead>
              <tr>
                <th>Bedrijf</th>
                <th>Email</th>
                <th>Abonnement</th>
                <th>Licenties</th>
                <th>Gebruikers</th>
                <th>Status</th>
                <th>Geregistreerd</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {bedrijven.map(bedrijf => (
                <tr key={bedrijf.id}>
                  <td><strong>{bedrijf.naam}</strong></td>
                  <td>{bedrijf.email}</td>
                  <td>
                    <span className={`badge ${getBadgeClass(bedrijf.abonnement)}`}>
                      {bedrijf.abonnement?.charAt(0).toUpperCase() + bedrijf.abonnement?.slice(1)}
                    </span>
                  </td>
                  <td>
                    {bedrijf.werkorder_licentie && (
                      <span style={{ display: 'inline-block', background: '#3b82f6', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, marginRight: 4 }}>W</span>
                    )}
                    {bedrijf.forms_licentie && (
                      <span style={{ display: 'inline-block', background: '#9f7aea', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4 }}>F</span>
                    )}
                    {bedrijf.aes_enabled && (
                      <span style={{ display: 'inline-block', background: '#f59e0b', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, marginLeft: 4 }} title="AES Premium - SMS-OTP signing">💎</span>
                    )}
                    {!bedrijf.werkorder_licentie && !bedrijf.forms_licentie && (
                      <span style={{ color: '#a0aec0', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>{bedrijf.gebruiker_count || bedrijf.aantal_gebruikers || 0}</td>
                  <td>
                    <span className={`status-badge ${bedrijf.actief ? 'actief' : 'inactief'}`}>
                      {bedrijf.actief ? 'Actief' : 'Inactief'}
                    </span>
                  </td>
                  <td>{new Date(bedrijf.created_at).toLocaleDateString('nl-NL')}</td>
                  <td>
                    <button
                      onClick={() => handleToggleAes(bedrijf.id, bedrijf.aes_enabled)}
                      style={{ marginRight: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, background: bedrijf.aes_enabled ? '#f59e0b' : '#e5e7eb', color: bedrijf.aes_enabled ? 'white' : '#6b7280', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                      title={bedrijf.aes_enabled ? 'AES Premium aan - klik om uit te zetten' : 'AES Premium uit - klik om aan te zetten'}
                    >
                      💎 {bedrijf.aes_enabled ? 'AES aan' : 'AES uit'}
                    </button>
                    <button
                      onClick={() => handleDelete(bedrijf.id, bedrijf.naam)}
                      className="btn-danger-small"
                    >
                      Verwijder
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const NieuwBedrijfModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    naam: '', email: '', telefoonnummer: '',
    adres: '', postcode: '', plaats: '',
    abonnement: 'professional',
    product: 'beide',
    admin_voornaam: '', admin_achternaam: '',
    admin_username: '', admin_password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!form.naam || !form.email || !form.admin_voornaam || !form.admin_achternaam || !form.admin_username) {
      setErr('Vul alle verplichte velden in (gemarkeerd met *)');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        naam: form.naam.trim(),
        email: form.email.trim(),
        telefoonnummer: form.telefoonnummer.trim() || null,
        adres: form.adres.trim() || null,
        postcode: form.postcode.trim() || null,
        plaats: form.plaats.trim() || null,
        abonnement: form.abonnement,
        admin_username: form.admin_username.trim(),
        admin_voornaam: form.admin_voornaam.trim(),
        admin_achternaam: form.admin_achternaam.trim(),
        admin_password: form.admin_password.trim() || undefined,
        werkorder_licentie: form.product === 'beide',
        forms_licentie: true
      };
      const r = await api.post('/bedrijven/admin-create', payload);
      if (r.data?.success) {
        alert('Bedrijf "' + form.naam + '" aangemaakt! Welkomstmail naar ' + form.email);
        onCreated();
      } else {
        setErr(r.data?.message || 'Onbekende fout');
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const lab = { display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#1e293b' };
  const inp = { display: 'block', width: '100%', marginTop: 4, padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const sec = { fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 20, marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #e2e8f0' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, padding: 28, width: '100%', maxWidth: 560, marginTop: 40, marginBottom: 40 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700 }}>Nieuw bedrijf aanmaken</h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>De admin krijgt een welkomstmail om zelf het wachtwoord in te stellen.</p>

        <div style={sec}>Bedrijf</div>
        <label style={lab}>Naam *<input style={inp} value={form.naam} onChange={e => set('naam', e.target.value)} autoFocus /></label>
        <label style={lab}>Email *<input style={inp} type="email" value={form.email} onChange={e => set('email', e.target.value)} /></label>
        <label style={lab}>Telefoon<input style={inp} value={form.telefoonnummer} onChange={e => set('telefoonnummer', e.target.value)} /></label>
        <label style={lab}>Adres<input style={inp} value={form.adres} onChange={e => set('adres', e.target.value)} /></label>
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ ...lab, width: 140 }}>Postcode<input style={inp} value={form.postcode} onChange={e => set('postcode', e.target.value)} /></label>
          <label style={{ ...lab, flex: 1 }}>Plaats<input style={inp} value={form.plaats} onChange={e => set('plaats', e.target.value)} /></label>
        </div>

        <div style={sec}>Product</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <label style={{ flex: 1, padding: 14, border: form.product === 'beide' ? '2px solid #10b981' : '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', background: form.product === 'beide' ? '#ecfdf5' : 'white' }}>
            <input type="radio" checked={form.product === 'beide'} onChange={() => set('product', 'beide')} style={{ marginRight: 8 }} />
            <strong>WOMsys volledig</strong>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Werkorders + Forms</div>
          </label>
          <label style={{ flex: 1, padding: 14, border: form.product === 'forms' ? '2px solid #10b981' : '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', background: form.product === 'forms' ? '#ecfdf5' : 'white' }}>
            <input type="radio" checked={form.product === 'forms'} onChange={() => set('product', 'forms')} style={{ marginRight: 8 }} />
            <strong>Alleen Forms</strong>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Alleen Forms-product</div>
          </label>
        </div>
        <label style={lab}>Abonnement
          <select style={inp} value={form.abonnement} onChange={e => set('abonnement', e.target.value)}>
            <option value="trial">Trial (14 dagen)</option>
            <option value="forms_klein">Forms Klein — €29/mnd (tot 100/mnd)</option>
            <option value="forms_groot">Forms Groot — €79/mnd (onbeperkt)</option>
            <option value="starter">Starter — €199/mnd</option>
            <option value="professional">Professional — €399/mnd</option>
            <option value="business">Business — €699/mnd</option>
            <option value="enterprise">Enterprise — op aanvraag</option>
          </select>
        </label>

        <div style={sec}>Admin-gebruiker</div>
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ ...lab, flex: 1 }}>Voornaam *<input style={inp} value={form.admin_voornaam} onChange={e => set('admin_voornaam', e.target.value)} /></label>
          <label style={{ ...lab, flex: 1 }}>Achternaam *<input style={inp} value={form.admin_achternaam} onChange={e => set('admin_achternaam', e.target.value)} /></label>
        </div>
        <label style={lab}>Gebruikersnaam *<input style={inp} value={form.admin_username} onChange={e => set('admin_username', e.target.value)} /></label>
        <label style={lab}>Wachtwoord (leeg = klant stelt zelf in via mail)<input style={inp} type="text" value={form.admin_password} onChange={e => set('admin_password', e.target.value)} /></label>

        {err && <div style={{ marginTop: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
          <button onClick={onClose} disabled={submitting} style={{ padding: '10px 16px', border: '1px solid #cbd5e1', background: 'white', borderRadius: 6, cursor: 'pointer' }}>Annuleren</button>
          <button onClick={submit} disabled={submitting} style={{ padding: '10px 20px', border: 'none', background: '#10b981', color: 'white', borderRadius: 6, cursor: 'pointer', fontWeight: 600, opacity: submitting ? 0.6 : 1 }}>{submitting ? 'Aanmaken...' : 'Bedrijf aanmaken'}</button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
